/**
 * AdminInvitesPage - Invite token management interface
 *
 * Feature: 647-invite-registration
 * User Story 1: Admin Creates Invite Link
 */

import { useState } from 'react';
import {
  useInviteTokensGraphQL,
  useCreateInviteTokenGraphQL,
  InviteToken,
} from '../../hooks/graphql/useInviteGraphQL';
import logger from '../../utils/logger';

interface CreateInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (url: string, nonce: string) => void;
}

function CreateInviteModal({ isOpen, onClose, onCreated }: CreateInviteModalProps) {
  const [maxUses, setMaxUses] = useState<number>(1000);
  const [allowsAdminEmail, setAllowsAdminEmail] = useState<boolean>(false);
  const { createInviteToken, loading, error } = useCreateInviteTokenGraphQL();

  if (!isOpen) return null;

  const handleCreate = async () => {
    try {
      const result = await createInviteToken(maxUses, allowsAdminEmail);
      if (result) {
        onCreated(result.inviteUrl, result.nonce);
        // Reset form for next use
        setAllowsAdminEmail(false);
      }
    } catch (err) {
      logger.error('AdminInvites', 'Failed to create invite token', err);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-surface-1 border border-border rounded-lg p-6 w-full max-w-md shadow-xl">
        <h2 className="text-xl font-semibold text-foreground mb-4">Create Invite Link</h2>

        <div className="space-y-4">
          <div>
            <label htmlFor="maxUses" className="block text-sm font-medium text-foreground mb-1">
              Max Uses
            </label>
            <input
              id="maxUses"
              type="number"
              min="1"
              value={maxUses}
              onChange={(e) => setMaxUses(parseInt(e.target.value, 10) || 1)}
              className="w-full px-3 py-2 bg-background border border-border rounded-md text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            />
            <p className="text-sm text-muted mt-1">
              Number of times this invite can be used for registration
            </p>
          </div>

          <div className="flex items-start gap-3">
            <input
              id="allowsAdminEmail"
              type="checkbox"
              checked={allowsAdminEmail}
              onChange={(e) => setAllowsAdminEmail(e.target.checked)}
              className="mt-1 h-4 w-4 rounded border-border bg-background text-primary focus:ring-2 focus:ring-primary"
            />
            <div>
              <label htmlFor="allowsAdminEmail" className="block text-sm font-medium text-foreground">
                Allow admin email registration
              </label>
              <p className="text-sm text-muted mt-0.5">
                Allows users to register with @206mp.com email addresses (admin accounts)
              </p>
            </div>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-md p-3">
              <p className="text-sm text-red-500">
                {error.message || 'Failed to create invite token'}
              </p>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-border rounded-md hover:bg-surface-2 transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCreate}
            disabled={loading}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors disabled:opacity-50"
          >
            {loading ? 'Creating...' : 'Create Invite'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface CopyInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  inviteUrl: string;
}

function CopyInviteModal({ isOpen, onClose, inviteUrl }: CopyInviteModalProps) {
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      logger.error('AdminInvites', 'Failed to copy to clipboard', err);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-surface-1 border border-border rounded-lg p-6 w-full max-w-lg shadow-xl">
        <h2 className="text-xl font-semibold text-foreground mb-4">Invite Created!</h2>

        <div className="space-y-4">
          <p className="text-sm text-muted">
            Share this URL with users you want to invite. They can use it to register an account.
          </p>

          <div className="flex gap-2">
            <input
              type="text"
              readOnly
              value={inviteUrl}
              className="flex-1 px-3 py-2 bg-background border border-border rounded-md text-foreground font-mono text-sm"
            />
            <button
              type="button"
              onClick={handleCopy}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            >
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>

          <p className="text-sm text-amber-500">
            This URL will only be shown once. Make sure to copy it now.
          </p>
        </div>

        <div className="flex justify-end mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

interface InviteTokenRowProps {
  token: InviteToken;
}

function InviteTokenRow({ token }: InviteTokenRowProps) {
  const statusBadge = token.isActive ? (
    <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
      Active
    </span>
  ) : (
    <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400">
      Exhausted
    </span>
  );

  const adminBadge = token.allowsAdminEmail ? (
    <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
      Admin
    </span>
  ) : null;

  return (
    <tr className="border-b border-border hover:bg-surface-2/50">
      <td className="px-4 py-3 text-sm font-mono text-foreground">
        {token.id.slice(0, 12)}...
      </td>
      <td className="px-4 py-3 text-sm text-foreground">
        {token.createdBy?.displayName || token.createdBy?.username || 'Unknown'}
      </td>
      <td className="px-4 py-3 text-sm text-foreground">
        {token.useCount} / {token.maxUses}
      </td>
      <td className="px-4 py-3 text-sm text-foreground">
        {token.usesRemaining}
      </td>
      <td className="px-4 py-3 text-sm">
        <div className="flex gap-2">
          {statusBadge}
          {adminBadge}
        </div>
      </td>
      <td className="px-4 py-3 text-sm text-muted">
        {new Date(token.createdAt).toLocaleDateString()}
      </td>
    </tr>
  );
}

export function AdminInvitesPage() {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [createdInviteUrl, setCreatedInviteUrl] = useState('');

  const { inviteTokens, loading, error, refetch } = useInviteTokensGraphQL();

  const handleInviteCreated = (url: string) => {
    setCreatedInviteUrl(url);
    setCreateModalOpen(false);
    setCopyModalOpen(true);
    refetch();
  };

  const handleCopyModalClose = () => {
    setCopyModalOpen(false);
    setCreatedInviteUrl('');
  };

  // Calculate stats
  const totalTokens = inviteTokens.length;
  const activeTokens = inviteTokens.filter(t => t.isActive).length;
  const totalRegistrations = inviteTokens.reduce((sum, t) => sum + t.useCount, 0);

  return (
    <div className="p-8 overflow-auto">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Invite Management</h1>
            <p className="text-muted mt-1">Create and manage invite links for new users</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setCreateModalOpen(true)}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
            >
              Create Invite
            </button>
          </div>
        </div>

        {/* Stats Panel */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-surface-1 border border-border rounded-lg p-4">
            <p className="text-sm text-muted">Total Invites</p>
            <p className="text-2xl font-bold text-foreground">{totalTokens}</p>
          </div>
          <div className="bg-surface-1 border border-border rounded-lg p-4">
            <p className="text-sm text-muted">Active Invites</p>
            <p className="text-2xl font-bold text-foreground">{activeTokens}</p>
          </div>
          <div className="bg-surface-1 border border-border rounded-lg p-4">
            <p className="text-sm text-muted">Total Registrations</p>
            <p className="text-2xl font-bold text-foreground">{totalRegistrations}</p>
          </div>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-red-500 font-semibold mb-1">Failed to Load Invites</h3>
                <p className="text-sm text-muted">
                  {error.message || 'An error occurred while loading invite tokens'}
                </p>
              </div>
              <button
                onClick={() => refetch()}
                className="px-4 py-2 border border-border rounded-md hover:bg-surface-2 transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {/* Invite Table */}
        <div className="bg-surface-1 border border-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-surface-2">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Token ID</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Created By</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Usage</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Remaining</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Status</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-foreground">Created</th>
                </tr>
              </thead>
              <tbody>
                {loading && inviteTokens.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-muted">
                      Loading invites...
                    </td>
                  </tr>
                ) : inviteTokens.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-muted">
                      No invite tokens found. Create one to get started.
                    </td>
                  </tr>
                ) : (
                  inviteTokens.map((token) => (
                    <InviteTokenRow key={token.id} token={token} />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modals */}
        <CreateInviteModal
          isOpen={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onCreated={handleInviteCreated}
        />
        <CopyInviteModal
          isOpen={copyModalOpen}
          onClose={handleCopyModalClose}
          inviteUrl={createdInviteUrl}
        />
      </div>
    </div>
  );
}
